import { Component, computed, effect, inject, linkedSignal } from '@angular/core';
import {
  SwitchBar,
  SwitchBarOption,
  Button,
  InputLabel,
  Select,
  Skeleton,
  SimpleDialog,
  SimpleDialogLayout,
  HelpButton,
} from 'excellence-ui';
import { NgIcon } from '@ng-icons/core';
import { SupplierContractsDetailsPage } from '../supplier-contracts-details-page/supplier-contracts-details-page';
import { TranslocoPipe } from '@jsverse/transloco';
import { form, FormField } from '@angular/forms/signals';
import { EnergyInterfaceLabelPipe } from '../../../energy-interfaces/pipes/energy-interface-label-pipe';
import { EnergyInterfacesStore } from '../../../energy-interfaces/stores/energy-interfaces.store';
import { PriceTableView } from '../../components/supplier-contract-price-table/definitions';
import { SupplierContractPriceTable } from '../../components/supplier-contract-price-table/supplier-contract-price-table';
import { SupplierContractPriceTotalCard } from '../../components/supplier-contract-price-total-card/supplier-contract-price-total-card';
import { getAvailableYears } from './definitions';
import { ErrorComponent } from '../../../../shared/components/error-component/error-component';
import {
  ForecastKey,
  SupplierContractForecastStore,
} from '../../stores/supplier-contract-forecast.store';
import { toHttpError } from '../../../../core/helper/http-error-type-guard';
import { SupplierContractPriceTableSkeleton } from '../../components/supplier-contract-price-table-skeleton/supplier-contract-price-table-skeleton';
import { SupplierContractPriceTotalCardSkeleton } from '../../components/supplier-contract-price-total-card-skeleton/supplier-contract-price-total-card-skeleton';

@Component({
  selector: 'app-supplier-contracts-details-price-page',
  imports: [
    SwitchBar,
    SwitchBarOption,
    NgIcon,
    Button,
    TranslocoPipe,
    InputLabel,
    FormField,
    Select,
    EnergyInterfaceLabelPipe,
    Skeleton,
    SupplierContractPriceTable,
    SupplierContractPriceTotalCard,
    SimpleDialog,
    SimpleDialogLayout,
    HelpButton,
    ErrorComponent,
    SupplierContractPriceTableSkeleton,
    SupplierContractPriceTotalCardSkeleton,
  ],
  templateUrl: './supplier-contracts-details-price-page.html',
})
export class SupplierContractsDetailsPricePage {
  private energyInterfacesStore = inject(EnergyInterfacesStore);
  private forecastStore = inject(SupplierContractForecastStore);
  private detailsComponent = inject(SupplierContractsDetailsPage);

  protected organizationID = computed(() => this.detailsComponent.organizationId());
  protected supplierContractID = computed(() => this.detailsComponent.supplierContractId());

  protected years = computed(() => {
    const contract = this.supplierContract();
    return contract ? getAvailableYears(contract) : [];
  });

  private filterModel = linkedSignal<{
    year: string;
    energyInterfaceID: string;
    view: PriceTableView;
  }>(() => ({
    year: this.years()[0] ?? new Date().getFullYear().toString(),
    energyInterfaceID: 'ALL',
    view: 'PRICE',
  }));

  protected filterForm = form(this.filterModel);

  protected supplierContract = computed(() => this.detailsComponent.state()?.data);
  protected energyInterfacesLoading = this.energyInterfacesStore.isLoading;
  protected energyInterfaces = computed(() => this.supplierContract()?.energyInterfaces || []);

  protected pricingStatus = computed(() => this.supplierContract()?.pricingStatus);

  private energyInterfaceID = computed(() => {
    const id = this.filterModel().energyInterfaceID;
    return id === 'ALL' ? undefined : id;
  });

  private forecastKey = computed<ForecastKey>(
    () =>
      `${this.supplierContractID()}-${parseInt(this.filterModel().year, 10)}-${this.energyInterfaceID() ?? 'ALL'}`,
  );

  protected prices = computed(() => this.forecastStore.prices()[this.forecastKey()]);
  protected cost = computed(() => this.forecastStore.cost()[this.forecastKey()]);

  protected isLoading = computed(() =>
    this.filterModel().view === 'PRICE'
      ? !this.prices() || this.prices()?.isLoading
      : !this.cost() || this.cost()?.isLoading,
  );
  protected error = computed(() =>
    this.filterModel().view === 'PRICE' ? this.prices()?.error : this.cost()?.error,
  );
  protected errorStatus = computed(() => toHttpError(this.error())?.status ?? null);

  constructor() {
    effect(() => {
      const key: ForecastKey = this.forecastKey();

      const id = this.supplierContractID();
      const filter = this.filterModel();
      const year = parseInt(filter.year, 10);
      const energyInterfaceID =
        filter.energyInterfaceID === 'ALL' ? null : filter.energyInterfaceID;

      if (!this.forecastStore.prices()[key]) {
        this.forecastStore.loadPrice(id, year, energyInterfaceID);
      }
      if (!this.forecastStore.cost()[key]) {
        this.forecastStore.loadCost(id, year, energyInterfaceID);
      }
    });
  }

  protected onClickReload() {
    window.location.reload();
  }

  protected onClickRetry() {
    switch (this.filterModel().view) {
      case 'PRICE':
        this.forecastStore.loadPrice(
          this.supplierContractID(),
          parseInt(this.filterModel().year, 10),
          this.energyInterfaceID() ?? null,
        );
        break;
      case 'FORECAST':
        this.forecastStore.loadCost(
          this.supplierContractID(),
          parseInt(this.filterModel().year, 10),
          this.energyInterfaceID() ?? null,
        );
        break;
    }
  }
}
